import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Sparkles,
  Target,
  Users,
  MapPin,
  DollarSign,
  Clock,
  TrendingUp,
  Handshake,
  CheckCircle2,
  Coins,
  Pencil,
  User,
  Eye,
  EyeOff,
  Trash2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import GrantReadyDialog from "@/components/ideas/GrantReadyDialog";
import NewIdeaDialog from "@/components/ideas/NewIdeaDialog";
import PromoteToProjectButton from "@/components/projects/PromoteToProjectButton";
import FollowIdeaButton from "@/components/ideas/FollowIdeaButton";
import IdeaUpdatesTab from "@/components/ideas/IdeaUpdatesTab";
import IdeaAsksTab from "@/components/ideas/IdeaAsksTab";
import IdeaInboxTab from "@/components/ideas/IdeaInboxTab";
import IdeaCoachTab from "@/components/ideas/IdeaCoachTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SocialMeta from "@/components/social/SocialMeta";
import ShareBar from "@/components/social/ShareBar";

const Section = ({
  icon: Icon,
  title,
  children,
}: {
  icon: any;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="bg-card border border-border rounded-xl p-5">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4 text-primary" />
      <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide">
        {title}
      </h3>
    </div>
    <div className="text-sm text-muted-foreground whitespace-pre-wrap">{children}</div>
  </div>
);

const IdeaDetail = () => {
  const { ideaId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [projectEditOpen, setProjectEditOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data: idea, isLoading: ideaLoading } = useQuery({
    queryKey: ["idea-detail", ideaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ideas")
        .select("*")
        .eq("id", ideaId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      const { data: profile } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url")
        .eq("user_id", data.user_id)
        .maybeSingle();

      return { ...data, profile };
    },
    enabled: !!ideaId,
  });

  const { data: grant, isLoading: grantLoading } = useQuery({
    queryKey: ["idea-grant-details", ideaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("idea_grant_details")
        .select("*")
        .eq("idea_id", ideaId)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: !!ideaId && !!user,
  });

  const isOwner = !!user && !!idea && user.id === idea.user_id;
  const { data: newResponseCount } = useQuery({
    queryKey: ["idea-inbox-count", ideaId],
    queryFn: async () => {
      const { count } = await supabase
        .from("idea_ask_responses")
        .select("id", { count: "exact", head: true })
        .eq("idea_id", ideaId)
        .eq("status", "new");
      return count ?? 0;
    },
    enabled: !!ideaId && isOwner,
  });

  if (authLoading || ideaLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!idea) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SiteHeader />
        <main className="pt-32 pb-20 container mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-2xl font-bold mb-2">Idea not found</h1>
          <p className="text-muted-foreground mb-6">
            This idea may have been removed.
          </p>
          <Button onClick={() => navigate("/ideas")}>Back to Discover</Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      {idea.status === "published" && (
        <SocialMeta
          title={`${idea.title} — Emerging Market Discover`}
          description={idea.description ?? undefined}
          path={`/ideas/${idea.id}`}
          image={idea.image_url}
          type="article"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: idea.title,
            description: idea.description ?? undefined,
            image: idea.image_url ?? undefined,
            datePublished: idea.created_at,
            author: {
              "@type": "Person",
              name: idea.profile?.display_name ?? "Anonymous",
            },
            publisher: {
              "@type": "Organization",
              name: "The Emerging Market",
              url: "https://emrkt.co",
            },
          }}
        />
      )}
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <button
            onClick={() => navigate("/ideas")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Discover
          </button>

          {/* Idea header */}
          <div className="bg-card border border-border rounded-xl overflow-hidden mb-6">
            {idea.image_url && (
              <img
                src={idea.image_url}
                alt={idea.title}
                className="w-full max-h-96 object-cover"
              />
            )}
            <div className="p-6">
              <div className="flex flex-wrap gap-1.5 mb-3">
                <Badge variant="secondary">{idea.category}</Badge>
                {idea.status === "draft" && (
                  <Badge variant="outline" className="border-dashed text-muted-foreground">
                    Draft
                  </Badge>
                )}
                {idea.tags?.map((t: string) => (
                  <Badge key={t} variant="outline" className="text-xs">
                    {t}
                  </Badge>
                ))}
                {grant && (
                  <Badge className="gap-1 bg-primary/15 text-primary border border-primary/30 hover:bg-primary/20">
                    <Sparkles className="h-3 w-3" /> Grant-Ready
                  </Badge>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
                {idea.title}
              </h1>
              {idea.description && (
                <p className="text-muted-foreground leading-relaxed">
                  {idea.description}
                </p>
              )}
              {user?.id === idea.user_id && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setProjectEditOpen(true)}
                    className="gap-2"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit Project
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={statusUpdating}
                    onClick={async () => {
                      const next = idea.status === "published" ? "draft" : "published";
                      setStatusUpdating(true);
                      const { error } = await supabase
                        .from("ideas")
                        .update({ status: next })
                        .eq("id", idea.id);
                      setStatusUpdating(false);
                      if (error) {
                        toast.error("Failed to update status");
                        return;
                      }
                      toast.success(next === "published" ? "Idea published" : "Moved to drafts");
                      queryClient.invalidateQueries({ queryKey: ["idea-detail", ideaId] });
                      queryClient.invalidateQueries({ queryKey: ["ideas"] });
                    }}
                    className="gap-2"
                  >
                    {idea.status === "published" ? (
                      <>
                        <EyeOff className="h-3.5 w-3.5" /> Move to Draft
                      </>
                    ) : (
                      <>
                        <Eye className="h-3.5 w-3.5" /> Publish
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteOpen(true)}
                    className="gap-2 text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              )}
              {user?.id !== idea.user_id && (
                <div className="mt-4">
                  <FollowIdeaButton ideaId={idea.id} ownerId={idea.user_id} />
                </div>
              )}
              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border">
                <Link
                  to={`/profile/${idea.user_id}`}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  <div className="h-7 w-7 rounded-full bg-secondary flex items-center justify-center">
                    {idea.profile?.avatar_url ? (
                      <img
                        src={idea.profile.avatar_url}
                        alt=""
                        className="h-7 w-7 rounded-full object-cover"
                      />
                    ) : (
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground hover:text-primary">
                    {idea.profile?.display_name || "Anonymous"}
                  </span>
                </Link>
                <span className="text-sm text-muted-foreground">·</span>
                <span className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(idea.created_at), {
                    addSuffix: true,
                  })}
                </span>
              </div>
            </div>
          </div>

          {idea.status === "published" && (
            <div className="bg-card/40 border border-border rounded-xl px-5 py-4 mb-6">
              <ShareBar
                path={`/ideas/${idea.id}`}
                title={idea.title}
                text={idea.description ?? undefined}
              />
            </div>
          )}

          <Tabs defaultValue="updates" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="updates">Updates</TabsTrigger>
              <TabsTrigger value="help">Ways to Help</TabsTrigger>
              <TabsTrigger value="brief">Grant-Ready Brief</TabsTrigger>
              {isOwner && (
                <TabsTrigger value="coach" className="gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" /> Coach
                </TabsTrigger>
              )}
              {isOwner && (
                <TabsTrigger value="inbox" className="gap-2">
                  Inbox
                  {!!newResponseCount && newResponseCount > 0 && (
                    <Badge className="h-5 px-1.5 bg-primary text-primary-foreground">
                      {newResponseCount}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="updates">
              <IdeaUpdatesTab ideaId={idea.id} ownerId={idea.user_id} />
            </TabsContent>

            <TabsContent value="help">
              <IdeaAsksTab ideaId={idea.id} ownerId={idea.user_id} />
            </TabsContent>

            {isOwner && (
              <TabsContent value="coach">
                <IdeaCoachTab ideaId={idea.id} />
              </TabsContent>
            )}

            {isOwner && (
              <TabsContent value="inbox">
                <IdeaInboxTab ideaId={idea.id} />
              </TabsContent>
            )}

            <TabsContent value="brief">
          {!user ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <Sparkles className="h-8 w-8 text-primary mx-auto mb-3" />
              <h2 className="text-lg font-semibold mb-2">
                Sign in to view the Grant-Ready brief
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Funders, agencies, and shareholders can view structured grant
                details after signing in.
              </p>
              <Button onClick={() => navigate("/auth")}>Sign in</Button>
            </div>
          ) : grantLoading ? (
            <div className="bg-card border border-border rounded-xl h-40 animate-pulse" />
          ) : !grant ? (
            <div className="bg-card border border-border border-dashed rounded-xl p-8 text-center">
              <Sparkles className="h-8 w-8 text-primary mx-auto mb-3" />
              <h2 className="text-lg font-semibold mb-2">
                No Grant-Ready brief yet
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Help this idea become fundable by adding the structured details
                grantmakers look for.
              </p>
              <Button onClick={() => setEditOpen(true)} className="gap-2">
                <Sparkles className="h-4 w-4" /> Make This Grant-Ready
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-semibold">Grant-Ready Brief</h2>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditOpen(true)}
                  className="gap-2"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                {user && (
                  <PromoteToProjectButton
                    ideaId={idea.id}
                    ownerUserId={idea.user_id}
                    currentUserId={user.id}
                    hasGrantBrief={true}
                  />
                )}
              </div>

              {grant.problem_statement && (
                <Section icon={Target} title="Problem">
                  {grant.problem_statement}
                </Section>
              )}
              {grant.proposed_solution && (
                <Section icon={CheckCircle2} title="Proposed Solution">
                  {grant.proposed_solution}
                </Section>
              )}
              {grant.target_population && (
                <Section icon={Users} title="Target Population">
                  {grant.target_population}
                </Section>
              )}
              {grant.service_area && grant.service_area.length > 0 && (
                <Section icon={MapPin} title="Service Area">
                  <div className="flex flex-wrap gap-1.5">
                    {grant.service_area.map((c: string) => (
                      <Badge key={c} variant="outline" className="text-xs">
                        {c}
                      </Badge>
                    ))}
                  </div>
                </Section>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {grant.budget_range && (
                  <Section icon={DollarSign} title="Budget">
                    {grant.budget_range}
                  </Section>
                )}
                {grant.timeline && (
                  <Section icon={Clock} title="Timeline">
                    {grant.timeline}
                  </Section>
                )}
                {grant.stage && (
                  <Section icon={TrendingUp} title="Stage">
                    {grant.stage}
                  </Section>
                )}
              </div>

              {grant.partners && (
                <Section icon={Handshake} title="Partners">
                  {grant.partners}
                </Section>
              )}
              {grant.outcomes && (
                <Section icon={CheckCircle2} title="Measurable Outcomes">
                  {grant.outcomes}
                </Section>
              )}
              {grant.funding_sought && grant.funding_sought.length > 0 && (
                <Section icon={Coins} title="Funding Sought">
                  <div className="flex flex-wrap gap-1.5">
                    {grant.funding_sought.map((s: string) => (
                      <Badge key={s} variant="secondary" className="text-xs">
                        {s}
                      </Badge>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <SiteFooter />
      {user && (
        <GrantReadyDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          ideaId={idea.id}
          ideaTitle={idea.title}
          existing={grant}
        />
      )}
      {user?.id === idea.user_id && (
        <NewIdeaDialog
          open={projectEditOpen}
          onOpenChange={setProjectEditOpen}
          idea={{
            id: idea.id,
            title: idea.title,
            description: idea.description,
            category: idea.category,
            tags: idea.tags,
            image_url: idea.image_url,
          }}
        />
      )}
      {user?.id === idea.user_id && (
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this project?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes "{idea.title}" along with its votes, comments,
                and Grant-Ready brief. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={deleting}
                onClick={async (e) => {
                  e.preventDefault();
                  setDeleting(true);
                  const { error } = await supabase
                    .from("ideas")
                    .delete()
                    .eq("id", idea.id);
                  setDeleting(false);
                  if (error) {
                    toast.error(error.message || "Failed to delete project");
                    return;
                  }
                  toast.success("Project deleted");
                  setDeleteOpen(false);
                  queryClient.invalidateQueries({ queryKey: ["ideas"] });
                  navigate("/ideas");
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? "Deleting..." : "Delete project"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default IdeaDetail;